import { useContext } from 'react';
import { useState } from 'react';

import { GenerateSaleExportContext } from '@/lib/contexts/generate-sale-export.context';
import {
  DatasourceInstructionMode,
  AddWebsiteDataSourceToDealDocument,
} from '@/lib/graphql/generated/graphql';
import { trackEvent } from '@/lib/mixpanel';
import { dealRefetchAtom } from '@/states/deal.state';
import { useMutation } from '@apollo/client/react';
import { useAtomValue } from 'jotai';

import { useDealNotEditableHandler } from './useDealNotEditableHandler';

// Type personnalisé pour les erreurs du domaine
type DomainError = {
  code: string;
  message: string;
};

export type AddWebsiteDataSourceResult =
  | { success: true; id: string }
  | { success: false; domainError?: DomainError };

export const useAddWebsiteDataSource = () => {
  const generateSaleExport = useContext(GenerateSaleExportContext);
  const [addWebsiteDataSourceMutation, { loading, error: apolloError }] =
    useMutation(AddWebsiteDataSourceToDealDocument);
  const { handleIfDealNotEditable } = useDealNotEditableHandler();
  const dealRefetch = useAtomValue(dealRefetchAtom);

  const [domainError, setDomainError] = useState<DomainError | null>(null);

  const addWebsiteDataSource = async ({
    dealId,
    hubspotDealId,
    websiteUrl,
    productUrl,
    instructionMode,
  }: {
    dealId?: string;
    hubspotDealId: string;
    websiteUrl?: string;
    productUrl?: string;
    instructionMode?: DatasourceInstructionMode;
  }): Promise<AddWebsiteDataSourceResult> => {
    setDomainError(null);
    const trackedDealId = dealId ?? hubspotDealId;
    trackEvent('IMPORT_STARTED', { dealId: trackedDealId, source: 'website' });

    const { data } = await addWebsiteDataSourceMutation({
      variables: {
        hubspotDealId,
        dataSourceParams: {
          websiteUrl,
          productUrl,
        },
        instructionMode,
        ...(generateSaleExport && { generateSaleExport: true }),
      },
    });

    if (data?.addWebsiteDataSourceToDeal.__typename === 'MutationSuccess') {
      if (dealId) {
        await dealRefetch.fn();
      }
      trackEvent('IMPORT_COMPLETED', {
        dealId: trackedDealId,
        source: 'website',
      });
      return {
        success: true,
        id: data.addWebsiteDataSourceToDeal.id,
      };
    }
    if (data?.addWebsiteDataSourceToDeal.__typename === 'DomainException') {
      const code = data.addWebsiteDataSourceToDeal.code;
      trackEvent('IMPORT_FAILED', {
        dealId: trackedDealId,
        source: 'website',
        errorCode: code,
      });
      if (handleIfDealNotEditable(code)) {
        return { success: false };
      }
      const newDomainError = {
        code,
        message: `${code}`,
      };
      setDomainError(newDomainError);
      return { success: false, domainError: newDomainError };
    }

    trackEvent('IMPORT_FAILED', {
      dealId: trackedDealId,
      source: 'website',
      errorCode: 'UNKNOWN',
    });
    setDomainError({
      code: 'UNKNOWN',
      message: 'Unreachable code',
    });
    return { success: false };
  };

  const error = domainError ? new Error(domainError.message) : apolloError;

  return {
    addWebsiteDataSource,
    loading,
    error,
    domainError,
    isDomainError: Boolean(domainError),
  };
};
